import { HttpError, paginateLocally } from "@nasam/kernel";
import {
  ASSET_ALREADY_ASSIGNED,
  DRIVER_ALREADY_ASSIGNED,
  type DriverAssignment,
  type DriverAssignmentAsset,
  type DriverAssignmentBranch,
  type DriverAssignmentCreateInput,
  type DriverAssignmentHistoryAction,
  type DriverAssignmentHistoryItem,
  type DriverAssignmentHistoryQuery,
  type DriverAssignmentHistorySortBy,
  type DriverAssignmentSortBy,
  type DriverAssignmentsListQuery,
  type DriverAssignmentsRepository,
} from "../domain";
import {
  assetFixtures,
  branchFixtures,
  driverAssignmentFixtures,
  driverAssignmentHistoryFixtures,
  driverFixtures,
  type DriverFixture,
} from "../testing";

const DEFAULT_LIMIT = 10;

export interface MockDriverAssignmentsRepositoryOptions {
  readonly assignments?: readonly DriverAssignment[];
  readonly history?: readonly DriverAssignmentHistoryItem[];
  /** Every driver and asset in the company, assigned or not. */
  readonly drivers?: readonly DriverFixture[];
  readonly assets?: readonly DriverAssignmentAsset[];
  readonly branches?: readonly DriverAssignmentBranch[];
  /** Simulated network latency, so loading states show in the app. */
  readonly latencyMs?: number;
  readonly now?: () => Date;
}

function notFound(method: string, id: string): HttpError {
  return new HttpError("Driver assignment not found", {
    status: 404,
    code: undefined,
    method,
    url: `/driver-assignments/${id}`,
    data: undefined,
  });
}

function conflict(code: string, message: string): HttpError {
  return new HttpError(message, {
    status: 409,
    code,
    method: "POST",
    url: "/driver-assignments",
    data: undefined,
  });
}

function byName(locale: "en" | "ar") {
  return (a: { nameEn: string; nameAr: string }, b: typeof a) =>
    locale === "ar"
      ? a.nameAr.localeCompare(b.nameAr, "ar")
      : a.nameEn.localeCompare(b.nameEn, "en");
}

/** What the search and sort helpers read -- shared by assignments and
 *  history rows (whose asset has no code). */
interface Searchable {
  readonly driver: { readonly nameEn: string; readonly nameAr: string };
  readonly asset: {
    readonly nameEn: string;
    readonly nameAr: string;
    readonly code?: string;
  };
}

function sortValue(
  assignment: Searchable & { readonly assignedAt: string },
  sortBy: DriverAssignmentSortBy,
): string {
  switch (sortBy) {
    case "driverNameEn":
      return assignment.driver.nameEn;
    case "driverNameAr":
      return assignment.driver.nameAr;
    case "assetNameEn":
      return assignment.asset.nameEn;
    case "assetNameAr":
      return assignment.asset.nameAr;
    case "assignedAt":
      return assignment.assignedAt;
  }
}

function compareBy(sortBy: DriverAssignmentSortBy) {
  const locale = sortBy.endsWith("Ar") ? "ar" : "en";
  return (a: DriverAssignment, b: DriverAssignment) =>
    sortValue(a, sortBy).localeCompare(sortValue(b, sortBy), locale, {
      numeric: true,
      sensitivity: "base",
    });
}

function matchesSearch(
  assignment: Searchable,
  search?: string,
  extra = "",
) {
  const term = search?.trim().toLocaleLowerCase();
  if (term === undefined || term === "") return true;
  const { driver, asset } = assignment;
  return [
    driver.nameEn,
    driver.nameAr,
    asset.nameEn,
    asset.nameAr,
    asset.code ?? "",
    extra,
  ].some((value) => value.toLocaleLowerCase().includes(term));
}

function inDateRange(date: string, from?: string, to?: string) {
  const time = Date.parse(date);
  if (from !== undefined && time < Date.parse(from)) return false;
  if (to !== undefined && time > Date.parse(to)) return false;
  return true;
}

// The real endpoint searches the action server-side; the mock matches the
// code and both UI labels (en / ar) so typing either finds the rows.
const ACTION_KEYWORDS: Record<DriverAssignmentHistoryAction, string> = {
  Created: "created تم الإنشاء",
  Updated: "updated محول",
  Removed: "removed منتهي",
};

function historySortValue(
  item: DriverAssignmentHistoryItem,
  sortBy: DriverAssignmentHistorySortBy,
): string {
  if (sortBy === "actionAt") return item.actionAt;
  if (sortBy === "action") return item.action;
  // The driver/asset/assignedAt columns it shares with the list.
  return sortValue(item, sortBy);
}

/**
 * In-memory stand-in for the driver-assignment endpoints, which the backend
 * hasn't shipped yet. Searches, filters, sorts and pages the way the API is
 * expected to, so the screen won't change when the HTTP repository lands.
 */
export function createMockDriverAssignmentsRepository({
  assignments = driverAssignmentFixtures,
  history: seededHistory = driverAssignmentHistoryFixtures,
  drivers = driverFixtures,
  assets = assetFixtures,
  branches = branchFixtures,
  latencyMs = 0,
  now = () => new Date(),
}: MockDriverAssignmentsRepositoryOptions = {}): DriverAssignmentsRepository {
  let rows = [...assignments];
  const history = [...seededHistory];
  let nextId = rows.length + 1;
  let nextHistoryId = history.length + 1;

  // History story, scenario 6: every change writes one read-only row.
  function record(
    { driver, asset, assignedAt }: DriverAssignment,
    action: DriverAssignmentHistoryAction,
  ) {
    history.push({
      id: `history-${nextHistoryId++}`,
      driver,
      asset,
      action,
      assignedAt,
      actionAt: now().toISOString(),
    });
  }
  const wait = () =>
    latencyMs > 0
      ? new Promise<void>((resolve) => setTimeout(resolve, latencyMs))
      : Promise.resolve();

  /** Builds a new active assignment, enforcing one active assignment per
   *  driver and per asset. `replacing` is the one an update closes, so its
   *  own driver/asset don't count as taken. */
  function open(
    { driverId, assetId }: DriverAssignmentCreateInput,
    replacing?: string,
  ): DriverAssignment {
    const others = rows.filter((row) => row.id !== replacing);
    if (others.some((row) => row.driver.id === driverId)) {
      throw conflict(
        DRIVER_ALREADY_ASSIGNED,
        "The selected Driver is already assigned to another Asset.",
      );
    }
    if (others.some((row) => row.asset.id === assetId)) {
      throw conflict(
        ASSET_ALREADY_ASSIGNED,
        "The selected Asset already has an assigned Driver.",
      );
    }
    const driver = drivers.find((candidate) => candidate.id === driverId);
    const asset = assets.find((candidate) => candidate.id === assetId);
    if (driver === undefined || asset === undefined) {
      throw new HttpError("Driver or asset not found", {
        status: 404,
        code: undefined,
        method: "POST",
        url: "/driver-assignments",
        data: undefined,
      });
    }
    return {
      id: `driver-assignment-${nextId++}`,
      driver: {
        id: driver.id,
        username: driver.username,
        nameEn: driver.nameEn,
        nameAr: driver.nameAr,
        avatarUrl: driver.avatarUrl,
      },
      asset,
      // The system sets the assignment date, never the form.
      assignedAt: now().toISOString(),
    };
  }

  return {
    async list(query: DriverAssignmentsListQuery = {}) {
      await wait();
      const filtered = rows.filter(
        (row) =>
          matchesSearch(row, query.search) &&
          inDateRange(row.assignedAt, query.fromAssignedAt, query.toAssignedAt),
      );
      // Newest first unless the user picks a column.
      const sortBy = query.sortBy ?? "assignedAt";
      const direction = query.sortBy === undefined ? "desc" : query.sort;
      const sorted = [...filtered].sort(compareBy(sortBy));
      if (direction === "desc") sorted.reverse();
      return paginateLocally(
        sorted,
        query.page ?? 1,
        query.limit ?? DEFAULT_LIMIT,
      );
    },

    async getById(id: string) {
      await wait();
      const assignment = rows.find((row) => row.id === id);
      if (assignment === undefined) throw notFound("GET", id);
      return {
        id: assignment.id,
        driver: assignment.driver,
        // Every asset the driver currently operates, newest first.
        assets: rows
          .filter((row) => row.driver.id === assignment.driver.id)
          .sort(compareBy("assignedAt"))
          .reverse()
          .map((row) => ({
            assignmentId: row.id,
            asset: row.asset,
            assignedAt: row.assignedAt,
          })),
      };
    },

    async create(input) {
      await wait();
      const assignment = open(input);
      rows = [...rows, assignment];
      record(assignment, "Created");
      return { assignment, message: null };
    },

    async update(id, input) {
      await wait();
      const current = rows.find((row) => row.id === id);
      if (current === undefined) throw notFound("PUT", id);
      if (
        current.driver.id === input.driverId &&
        current.asset.id === input.assetId
      ) {
        return { assignment: current, message: null };
      }
      // Scenario 1: the old assignment closes into history, a new one opens.
      const assignment = open(input, id);
      rows = [...rows.filter((row) => row.id !== id), assignment];
      record(assignment, "Updated");
      return { assignment, message: null };
    },

    async delete(id: string) {
      await wait();
      const assignment = rows.find((row) => row.id === id);
      if (assignment === undefined) throw notFound("DELETE", id);
      rows = rows.filter((row) => row.id !== id);
      record(assignment, "Removed");
      return { message: null };
    },

    async listAvailableDrivers() {
      await wait();
      const assigned = new Set(rows.map((row) => row.driver.id));
      return drivers
        .filter(
          (driver) => driver.status === "ACTIVE" && !assigned.has(driver.id),
        )
        .map(({ id, username, nameEn, nameAr, avatarUrl }) => ({
          id,
          username,
          nameEn,
          nameAr,
          avatarUrl,
        }))
        .sort(byName("en"));
    },

    async listBranches() {
      await wait();
      return [...branches];
    },

    // The real endpoint scopes this to the caller (Supervisor: own assets);
    // the mock plays a Company Administrator and returns them all.
    async listAssignableAssets(query = {}) {
      await wait();
      return assets
        .filter(
          (asset) =>
            query.branchId === undefined || asset.branch.id === query.branchId,
        )
        .sort(byName("en"));
    },

    async listHistory(query: DriverAssignmentHistoryQuery = {}) {
      await wait();
      const filtered = history.filter(
        (item) =>
          matchesSearch(item, query.search, ACTION_KEYWORDS[item.action]) &&
          (query.action === undefined || item.action === query.action) &&
          inDateRange(item.assignedAt, query.fromAssignedAt, query.toAssignedAt) &&
          inDateRange(item.actionAt, query.fromActionAt, query.toActionAt),
      );
      // Latest action first unless the user picks a column.
      const sortBy = query.sortBy ?? "actionAt";
      const direction = query.sortBy === undefined ? "desc" : query.sort;
      const locale = sortBy.endsWith("Ar") ? "ar" : "en";
      const sorted = [...filtered].sort((a, b) =>
        historySortValue(a, sortBy).localeCompare(
          historySortValue(b, sortBy),
          locale,
          { numeric: true, sensitivity: "base" },
        ),
      );
      if (direction === "desc") sorted.reverse();
      return paginateLocally(
        sorted,
        query.page ?? 1,
        query.limit ?? DEFAULT_LIMIT,
      );
    },
  };
}
